import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from './use-toast';

export interface EmpIndicador {
  id: string;
  contrato_id: string;
  periodo_referencia: string;
  tipo_periodo: string;
  pv: number;
  ev: number;
  ac: number;
  spi: number | null;
  cpi: number | null;
  eac: number | null;
  etc: number | null;
  vac: number | null;
  percentual_fisico_previsto: number;
  percentual_fisico_realizado: number;
  percentual_financeiro_previsto: number;
  percentual_financeiro_realizado: number;
  desvio_prazo_dias: number;
  desvio_custo_percentual: number;
  baseline_bac: number | null;
  congelado: boolean;
  congelado_em: string | null;
  congelado_por: string | null;
  dados_extras: Record<string, any> | null;
  created_at: string;
  updated_at: string;
  /** Source identifier for debugging */
  _evm_source?: 'blindados' | 'epc_qtd';
}

export interface EmpDesvio {
  id: string;
  contrato_id: string;
  indicador_id: string | null;
  boletim_id: string | null;
  tipo: string;
  severidade: string;
  descricao: string;
  valor_referencia: number | null;
  valor_atual: number | null;
  desvio_percentual: number | null;
  impacto_estimado: string | null;
  acao_corretiva: string | null;
  responsavel_nome: string | null;
  responsavel_email: string | null;
  status: string;
  resolvido_por: string | null;
  resolvido_em: string | null;
  resolucao_descricao: string | null;
  dados_extras: Record<string, any> | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface EmpAlerta {
  id: string;
  contrato_id: string;
  regra_id: string | null;
  desvio_id: string | null;
  indicador_id: string | null;
  titulo: string;
  descricao: string | null;
  severidade: string;
  status: string;
  dados_contexto: Record<string, any> | null;
  reconhecido_por: string | null;
  reconhecido_em: string | null;
  resolvido_por: string | null;
  resolvido_em: string | null;
  notas_resolucao: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Fetch EPC-QTD EVM data from emp_vw_evm_contrato and map to EmpIndicador shape.
 * Returns null if data is empty or query fails (triggering fallback).
 */
async function fetchEpcQtdIndicadores(contratoId: string): Promise<EmpIndicador[] | null> {
  try {
    const { data, error } = await (supabase as any)
      .from('emp_vw_evm_contrato')
      .select('contrato_id, versao_id, data, pv_dia, pv, ev, bac, ac, spi, cpi, eac, etc, vac')
      .eq('contrato_id', contratoId)
      .order('data', { ascending: true });

    if (error || !data || data.length === 0) {
      console.debug('[EVM:HOOK] EVM_DATA_EMPTY for contract', contratoId);
      return null;
    }

    console.debug('[EVM:HOOK] EVM_SOURCE_SELECTED: epc_qtd, rows:', data.length);

    return data.map((row: any, idx: number) => ({
      id: `evm-${contratoId}-${idx}`,
      contrato_id: row.contrato_id,
      periodo_referencia: row.data,
      tipo_periodo: 'diario',
      pv: row.pv || 0,
      ev: row.ev || 0,
      ac: row.ac || 0,
      spi: row.spi,
      cpi: row.cpi,
      eac: row.eac,
      etc: row.etc,
      vac: row.vac,
      percentual_fisico_previsto: row.bac > 0 ? ((row.pv || 0) / row.bac) * 100 : 0,
      percentual_fisico_realizado: row.bac > 0 ? ((row.ev || 0) / row.bac) * 100 : 0,
      percentual_financeiro_previsto: row.bac > 0 ? ((row.pv || 0) / row.bac) * 100 : 0,
      percentual_financeiro_realizado: row.bac > 0 ? ((row.ac || 0) / row.bac) * 100 : 0,
      desvio_prazo_dias: 0,
      desvio_custo_percentual: 0,
      baseline_bac: row.bac,
      congelado: false,
      congelado_em: null,
      congelado_por: null,
      dados_extras: null,
      created_at: row.data,
      updated_at: row.data,
      _evm_source: 'epc_qtd' as const,
    }));
  } catch (err) {
    console.warn('[EVM:HOOK] EPC-QTD query exception, will fallback:', err);
    return null;
  }
}

/**
 * Fetch standard indicadores from emp_vw_indicadores_blindados.
 */
async function fetchBlindadosIndicadores(contratoId: string): Promise<EmpIndicador[]> {
  const { data, error } = await (supabase as any)
    .from('emp_vw_indicadores_blindados')
    .select('*')
    .eq('contrato_id', contratoId)
    .order('periodo_referencia', { ascending: true });
  if (error) throw error;
  return ((data || []) as EmpIndicador[]).map(d => ({
    ...d,
    _evm_source: 'blindados' as const,
  }));
}

export function useEmpIndicadores(contratoId: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Check EPC-QTD flag + contract type in a single query
  const { data: epcContext } = useQuery({
    queryKey: ['emp-epc-context', contratoId],
    queryFn: async () => {
      const [flagRes, contratoRes] = await Promise.all([
        (supabase as any)
          .from('emp_motor_flags')
          .select('ativo')
          .eq('flag_nome', 'epc_qtd_enabled')
          .maybeSingle(),
        supabase
          .from('emp_contratos')
          .select('tipo_contrato_operacional, tipo_medicao')
          .eq('id', contratoId)
          .maybeSingle(),
      ]);

      const flagEnabled = flagRes.data?.ativo === true;
      const tipo = contratoRes.data?.tipo_contrato_operacional;
      const tipoMed = contratoRes.data?.tipo_medicao;
      const isEpcContract = tipo === 'preco_global' || tipoMed === 'epc' || tipoMed === 'hibrido';

      const useEpcSource = flagEnabled && isEpcContract;
      console.debug('[EVM:HOOK]', useEpcSource ? 'EVM_SOURCE_SELECTED: epc_qtd' : 'EVM_FLAG_DISABLED or non-EPC contract');
      return { useEpcSource, flagEnabled };
    },
    enabled: !!contratoId,
    staleTime: 5 * 60 * 1000,
  });

  const useEpcSource = epcContext?.useEpcSource ?? false;

  const { data: indicadores = [], isLoading: indicadoresLoading } = useQuery({
    queryKey: ['emp-indicadores', contratoId, useEpcSource],
    queryFn: async () => {
      if (useEpcSource) {
        // Try EPC-QTD source first, fallback to blindados
        const epcData = await fetchEpcQtdIndicadores(contratoId);
        if (epcData && epcData.length > 0) {
          return epcData;
        }
        // Fallback: no EPC data available yet
        console.debug('[EVM:HOOK] EVM_DATA_EMPTY, falling back to blindados');
      }
      return fetchBlindadosIndicadores(contratoId);
    },
    enabled: !!contratoId && epcContext !== undefined,
  });

  const { data: desvios = [], isLoading: desviosLoading } = useQuery({
    queryKey: ['emp-desvios', contratoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_desvios')
        .select('*')
        .eq('contrato_id', contratoId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []) as EmpDesvio[];
    },
    enabled: !!contratoId,
  });

  const { data: alertas = [], isLoading: alertasLoading } = useQuery({
    queryKey: ['emp-alertas', contratoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_alertas')
        .select('*')
        .eq('contrato_id', contratoId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []) as EmpAlerta[];
    },
    enabled: !!contratoId,
  });

  // Treat deviation
  const tratarDesvio = useMutation({
    mutationFn: async ({ desvioId, acao, resolucao }: { desvioId: string; acao: string; resolucao?: string }) => {
      const updates: Record<string, any> = {
        acao_corretiva: acao,
        status: resolucao ? 'resolvido' : 'em_tratamento',
        updated_at: new Date().toISOString(),
      };
      if (resolucao) {
        updates.resolucao_descricao = resolucao;
        updates.resolvido_por = user?.id;
        updates.resolvido_em = new Date().toISOString();
      }
      const { error } = await supabase
        .from('emp_desvios')
        .update(updates)
        .eq('id', desvioId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emp-desvios', contratoId] });
      toast({ title: 'Desvio atualizado' });
    },
    onError: (e: any) => toast({ variant: 'destructive', title: 'Erro', description: e.message }),
  });

  // Acknowledge alert
  const reconhecerAlerta = useMutation({
    mutationFn: async (alertaId: string) => {
      const { error } = await supabase
        .from('emp_alertas')
        .update({
          status: 'reconhecido',
          reconhecido_por: user?.id,
          reconhecido_em: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', alertaId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emp-alertas', contratoId] });
      toast({ title: 'Alerta reconhecido' });
    },
    onError: (e: any) => toast({ variant: 'destructive', title: 'Erro', description: e.message }),
  });

  // Resolve alert
  const resolverAlerta = useMutation({
    mutationFn: async ({ alertaId, notas }: { alertaId: string; notas: string }) => {
      const { error } = await supabase
        .from('emp_alertas')
        .update({
          status: 'resolvido',
          resolvido_por: user?.id,
          resolvido_em: new Date().toISOString(),
          notas_resolucao: notas,
          updated_at: new Date().toISOString(),
        })
        .eq('id', alertaId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emp-alertas', contratoId] });
      toast({ title: 'Alerta resolvido' });
    },
    onError: (e: any) => toast({ variant: 'destructive', title: 'Erro', description: e.message }),
  });

  // Freeze indicator (SPI < 0.9 rule)
  const congelarIndicador = useMutation({
    mutationFn: async (indicadorId: string) => {
      const { error } = await supabase
        .from('emp_indicadores')
        .update({
          congelado: true,
          congelado_em: new Date().toISOString(),
          congelado_por: user?.id,
          updated_at: new Date().toISOString(),
        } as any)
        .eq('id', indicadorId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emp-indicadores', contratoId] });
      toast({ title: 'Indicador congelado' });
    },
    onError: (e: any) => toast({ variant: 'destructive', title: 'Erro', description: e.message }),
  });

  // Latest indicator
  const latest = indicadores.length > 0 ? indicadores[indicadores.length - 1] : null;

  return {
    indicadores,
    desvios,
    alertas,
    latest,
    isLoading: indicadoresLoading || desviosLoading || alertasLoading,
    tratarDesvio,
    reconhecerAlerta,
    resolverAlerta,
    congelarIndicador,
    /** Whether EPC-QTD source is active for this contract */
    isEpcQtdSource: useEpcSource && indicadores.length > 0 && indicadores[0]?._evm_source === 'epc_qtd',
  };
}
